import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  Layers, FolderTree, Tag, Building2, Briefcase, MapPin,
  Plus, Edit, Trash2, ChevronRight, BarChart3
} from "lucide-react";
import axios from "axios";

const CostCenters = () => {
  const { token } = useAuth();
  const [dimensions, setDimensions] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [selectedType, setSelectedType] = useState("cost_center");
  const [showDialog, setShowDialog] = useState(false);
  const [editingDimension, setEditingDimension] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    dimension_type: "cost_center",
    code: "",
    name: "",
    parent_id: "",
    is_active: true
  });

  const dimensionTypes = [
    { value: "cost_center", label: "Kostnadsställe", icon: Building2 },
    { value: "project", label: "Projekt", icon: Briefcase },
    { value: "department", label: "Avdelning", icon: FolderTree },
    { value: "region", label: "Region", icon: MapPin },
    { value: "custom", label: "Egen dimension", icon: Tag }
  ];

  useEffect(() => {
    fetchDimensions();
  }, [selectedType]);

  const fetchDimensions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dimensions?dimension_type=${selectedType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDimensions(res.data.dimensions);
      setHierarchy(res.data.hierarchy);
    } catch (error) {
      console.error('Error fetching dimensions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const res = await axios.get(
        `${API}/reports/by-dimension?dimension_type=${selectedType}&start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportData(res.data);
    } catch (error) {
      toast.error("Kunde inte hämta rapport");
    }
  };

  const saveDimension = async () => {
    if (!form.code || !form.name) {
      toast.error("Fyll i kod och namn");
      return;
    }

    try {
      if (editingDimension) {
        await axios.put(`${API}/dimensions/${editingDimension.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Dimension uppdaterad");
      } else {
        await axios.post(`${API}/dimensions`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Dimension skapad");
      }
      setShowDialog(false);
      resetForm();
      fetchDimensions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte spara dimension");
    }
  };

  const deleteDimension = async (id) => {
    if (!window.confirm("Är du säker på att du vill ta bort denna dimension?")) return;

    try {
      await axios.delete(`${API}/dimensions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Dimension borttagen");
      fetchDimensions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort dimension");
    }
  };

  const resetForm = () => {
    setForm({
      dimension_type: selectedType,
      code: "",
      name: "",
      parent_id: "",
      is_active: true
    });
    setEditingDimension(null);
  };

  const openEditDialog = (dim) => {
    setEditingDimension(dim);
    setForm({
      dimension_type: dim.dimension_type,
      code: dim.code,
      name: dim.name,
      parent_id: dim.parent_id || "",
      is_active: dim.is_active
    });
    setShowDialog(true);
  };

  const openNewDialog = () => {
    resetForm();
    setForm(prev => ({ ...prev, dimension_type: selectedType }));
    setShowDialog(true);
  };

  const getTypeLabel = (type) => {
    const found = dimensionTypes.find(t => t.value === type);
    return found?.label || type;
  };

  const getTypeIcon = (type) => {
    const found = dimensionTypes.find(t => t.value === type);
    const Icon = found?.icon || Layers;
    return <Icon className="h-4 w-4" />;
  };

  const renderHierarchy = (items, level = 0) => {
    return items.map(item => (
      <div key={item.id}>
        <div 
          className={`flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg ${level > 0 ? 'ml-6' : ''}`}
          style={{ marginLeft: level * 24 }}
        >
          <div className="flex items-center gap-3">
            {item.children?.length > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded">{item.code}</span>
                <span className="font-medium">{item.name}</span>
              </div>
              {item.full_path && item.full_path !== item.name && (
                <p className="text-xs text-muted-foreground">{item.full_path}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!item.is_active && <Badge variant="secondary">Inaktiv</Badge>}
            <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => deleteDimension(item.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {item.children?.length > 0 && renderHierarchy(item.children, level + 1)}
      </div>
    ));
  };

  return (
    <div className="p-6 space-y-6" data-testid="cost-centers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Kostnadsställen & Dimensioner</h1>
          <p className="text-muted-foreground">Kategorisera bokföring för detaljerad uppföljning</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={openNewDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Ny dimension
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingDimension ? 'Redigera' : 'Skapa'} dimension</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Typ</Label>
                <Select value={form.dimension_type} onValueChange={(v) => setForm({ ...form, dimension_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dimensionTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kod</Label>
                  <Input 
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="KS01"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Namn</Label>
                  <Input 
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Försäljning"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Överordnad (valfritt)</Label>
                <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ingen överordnad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ingen överordnad</SelectItem>
                    {dimensions.filter(d => d.id !== editingDimension?.id).map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.code} - {d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
                <Label>Aktiv</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Avbryt</Button>
              <Button onClick={saveDimension}>{editingDimension ? 'Uppdatera' : 'Skapa'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        {dimensionTypes.map(type => {
          const count = dimensions.filter(d => d.dimension_type === type.value).length || 
                        (selectedType === type.value ? dimensions.length : 0);
          const Icon = type.icon;
          return (
            <Card 
              key={type.value}
              className={`cursor-pointer transition-colors ${selectedType === type.value ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
              onClick={() => setSelectedType(type.value)}
            >
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{type.label}</p>
                    <p className="text-2xl font-bold">{selectedType === type.value ? dimensions.length : '-'}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${selectedType === type.value ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list">Dimensioner</TabsTrigger>
          <TabsTrigger value="report">Rapport</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getTypeIcon(selectedType)}
                {getTypeLabel(selectedType)}
              </CardTitle>
              <CardDescription>
                {dimensions.length} dimension{dimensions.length !== 1 ? 'er' : ''} registrerade
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hierarchy.length > 0 ? (
                <div className="space-y-1">
                  {renderHierarchy(hierarchy)}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Inga {getTypeLabel(selectedType).toLowerCase()} skapade</p>
                  <Button variant="outline" className="mt-4" onClick={openNewDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Skapa din första
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="report" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Rapport per {getTypeLabel(selectedType).toLowerCase()}
                  </CardTitle>
                  <CardDescription>Bokföring uppdelad per dimension</CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap">Från:</Label>
                    <Input 
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      className="w-40"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap">Till:</Label>
                    <Input 
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      className="w-40"
                    />
                  </div>
                  <Button onClick={fetchReport}>Hämta rapport</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {reportData ? (
                <div className="space-y-4">
                  <div className="border rounded-lg">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-3 text-left">Kod</th>
                          <th className="p-3 text-left">Namn</th>
                          <th className="p-3 text-right">Debet</th>
                          <th className="p-3 text-right">Kredit</th>
                          <th className="p-3 text-right">Netto</th>
                          <th className="p-3 text-right">Antal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.by_dimension?.map(dim => (
                          <tr key={dim.dimension_id} className="border-t hover:bg-muted/30">
                            <td className="p-3 font-mono">{dim.dimension_code}</td>
                            <td className="p-3">{dim.dimension_name}</td>
                            <td className="p-3 text-right font-mono">{dim.total_debit.toLocaleString('sv-SE')}</td>
                            <td className="p-3 text-right font-mono">{dim.total_credit.toLocaleString('sv-SE')}</td>
                            <td className={`p-3 text-right font-mono font-semibold ${dim.net >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {dim.net.toLocaleString('sv-SE')}
                            </td>
                            <td className="p-3 text-right">{dim.entry_count}</td>
                          </tr>
                        ))}
                        {reportData.unassigned?.entry_count > 0 && (
                          <tr className="border-t bg-muted/30">
                            <td className="p-3 font-mono">-</td>
                            <td className="p-3 italic text-muted-foreground">Ej tilldelat</td>
                            <td className="p-3 text-right font-mono">{reportData.unassigned.total_debit.toLocaleString('sv-SE')}</td>
                            <td className="p-3 text-right font-mono">{reportData.unassigned.total_credit.toLocaleString('sv-SE')}</td>
                            <td className="p-3 text-right font-mono">{reportData.unassigned.net.toLocaleString('sv-SE')}</td>
                            <td className="p-3 text-right">{reportData.unassigned.entry_count}</td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-muted/50 font-semibold">
                        <tr className="border-t">
                          <td className="p-3" colSpan={2}>Totalt</td>
                          <td className="p-3 text-right font-mono">{reportData.totals?.total_debit.toLocaleString('sv-SE')}</td>
                          <td className="p-3 text-right font-mono">{reportData.totals?.total_credit.toLocaleString('sv-SE')}</td>
                          <td className="p-3"></td>
                          <td className="p-3"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Klicka på "Hämta rapport" för att se data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CostCenters;
